// P:\0PJ\synforge\frontend\app\page.tsx v2 2026.06.22
"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { Zap, Users, BarChart2, ArrowRight, CheckCircle, Sparkles } from "lucide-react"

export default function LandingPage() {
  const router = useRouter()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // 로그인된 사용자는 대시보드로
    const token = localStorage.getItem("access_token")
    if (token) router.push("/dashboard")
  }, [])

  if (!mounted) return null

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">

      {/* 네비 */}
      <nav className="border-b border-white/[0.06] px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/logo_full.png" alt="Synforge" className="h-8 object-contain" />
        </div>
        <div className="flex items-center gap-3">
          <Link href="/login"
            className="text-white/50 hover:text-white text-sm transition-colors px-4 py-2">
            Sign in
          </Link>
          <Link href="/register"
            className="bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Get started
          </Link>
        </div>
      </nav>

      {/* 히어로 */}
      <section className="px-8 py-24 text-center max-w-4xl mx-auto w-full">
        <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 text-purple-300 text-xs font-medium mb-8">
          <Sparkles size={11} />
          ONE HUMAN. AN INFINITE TEAM.
        </div>
        <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
          Let multiple AIs debate<br />
          <span className="text-purple-400">your toughest decisions</span>
        </h1>
        <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
          Synforge brings GPT, Claude, Gemini, and Grok into a structured panel discussion.
          Get collective intelligence — not just one AI's opinion.
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link href="/register"
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            Start for free <ArrowRight size={16} />
          </Link>
          <Link href="/login"
            className="text-white/50 hover:text-white text-sm transition-colors px-4 py-3">
            Sign in →
          </Link>
        </div>
      </section>

      {/* 기능 */}
      <section className="px-8 py-20 max-w-5xl mx-auto">
        <div className="grid grid-cols-3 gap-6">
          {[
            {
              icon: <Users size={20} className="text-purple-400" />,
              title: "4 AI Panel",
              desc: "GPT, Claude, Gemini, and Grok debate your agenda simultaneously with different perspectives.",
            },
            {
              icon: <BarChart2 size={20} className="text-blue-400" />,
              title: "Consensus Score",
              desc: "Real-time agreement percentage shows how aligned the AIs are on your topic.",
            },
            {
              icon: <Zap size={20} className="text-yellow-400" />,
              title: "Actionable Output",
              desc: "Get structured results — research proposals, code, plans, images, or documents.",
            },
          ].map((f) => (
            <div key={f.title} className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6">
              <div className="w-10 h-10 bg-white/[0.05] rounded-xl flex items-center justify-center mb-4">
                {f.icon}
              </div>
              <h3 className="text-white font-semibold mb-2">{f.title}</h3>
              <p className="text-white/40 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 플랜 */}
      <section className="px-8 py-20 max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-white text-center mb-12">Simple pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              name: "Free",
              price: "$0",
              features: ["3 meetings/month", "1 round per meeting", "Basic output types"],
              cta: "Get started",
              highlight: false,
            },
            {
              name: "Pro",
              price: "$19",
              features: ["Unlimited meetings", "Up to 3 rounds", "All output types", "Priority AI access"],
              cta: "Start Pro",
              highlight: true,
            },
            {
              name: "Enterprise",
              price: "Custom",
              features: ["Everything in Pro", "Team collaboration", "Custom AI models", "Dedicated support"],
              cta: "Contact us",
              highlight: false,
            },
          ].map((plan) => (
            <div key={plan.name}
              className={`border rounded-2xl p-6 ${plan.highlight
                ? "bg-purple-600/10 border-purple-500/40"
                : "bg-[#0f0f18] border-white/[0.07]"
                }`}>
              {plan.highlight && (
                <div className="text-xs text-purple-300 font-medium mb-3">Most Popular</div>
              )}
              <div className="text-white font-semibold mb-1">{plan.name}</div>
              <div className="text-3xl font-bold text-white mb-6">
                {plan.price}<span className="text-sm text-white/40 font-normal">{plan.price !== "Custom" ? "/mo" : ""}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/60">
                    <CheckCircle size={13} className="text-purple-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link href="/register"
                className={`block text-center py-2.5 rounded-lg text-sm font-medium transition-colors ${plan.highlight
                  ? "bg-purple-600 hover:bg-purple-500 text-white"
                  : "bg-white/[0.05] hover:bg-white/[0.08] text-white/70"
                  }`}>
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* 푸터 */}
      <footer className="border-t border-white/[0.06] px-8 py-8 text-center">
        <p className="text-white/20 text-sm">© 2026 Synforge. All rights reserved.</p>
      </footer>
    </div >
  )
}
